import orderBy from 'lodash/orderBy';
import flattenDeep from 'lodash/flattenDeep';
import compact from 'lodash/compact';
import find from 'lodash/find';
import keys from 'lodash/keys';
import map from 'lodash/map';
import startsWith from 'lodash/startsWith';
import { readWM, create, read } from 'utils/Api';

import * as types from 'constants/ActionTypes';

export const requestQuestionnaire = RootQAElementID => ({
  type: types.COURSE_QUEST_REQUEST,
  RootQAElementID,
});

export const requestQuestionnaireSuccess = ({ questionnaire }) => ({
  type: types.COURSE_QUEST_SUCCESS,
  questionnaire,
});

export const requestQuestionnaireFailure = questionnaireError => ({
  type: types.COURSE_QUEST_FAILURE,
  questionnaireError,
});

export function sortChildren(obj) {
  if (obj.Children) {
    return { ...obj.Entity, ...obj, Children: orderBy(obj.Children.map(sortChildren), ['Order'], ['asc']) };
  }
  return { ...obj.Entity, ...obj };
}

const flattenChildren = (obj) => {
  if (obj.Children) {
    return [obj, ...flattenDeep(obj.Children.map(flattenChildren))];
  }
  return obj;
};

export function fetchOptions(qae, token) {
  const { QAInputID } = qae;
  const config = {
    entity: 'QAInput',
    entityID: QAInputID,
    headers: { Authorization: `Bearer ${token}` },
  };

  return read(config)
    .then(({ data }) => ({ ...qae, QAInput: data }));
}

/**
 * Takes in the id of the root element and collects
 * the needed data to construct tehe questionnaire.
 *
 * @param {number} RootQAElementID
 * The root QAElement to build the questionnaire from
 */
export const fetchQuestionnaire = (RootQAElementID, onSuccess) =>
  (dispatch, getState) => {
    dispatch(requestQuestionnaire(RootQAElementID));
    const { loginReducer: { user: { token } } } = getState();
    if (!RootQAElementID) {
      return dispatch(requestQuestionnaireFailure(new Error('Engar viðbótaspurningar eru skráð á þetta námskeið.')));
    }
    const configElement = {
      entity: 'QAElement',
      entityID: RootQAElementID,
      headers: { Authorization: `Bearer ${token}` },
    };
    return readWM(configElement)
      .then((QAElement) => {
        if (!QAElement.data) {
          return dispatch(requestQuestionnaireFailure(new Error('Námskeiðsmatið er ekki með neinar spurningar.')));
        }
        if (!QAElement.data.Children || !QAElement.data.Children.length) {
          return dispatch(onSuccess());
        }
        const initQAElements = flattenChildren(sortChildren(QAElement.data));
        const QAElements = initQAElements.map((e) => {
          if (e.QAInput) {
            return { ...e, ...e.Entity, QAInput: { ...e.QAInput.Entity, ...e.QAInput } };
          }
          return { ...e, ...e.Entity };
        });
        return dispatch(requestQuestionnaireSuccess({
          questionnaire: {
            RootQAElementID,
            QAElement: sortChildren(QAElement.data),
            QAElements,
          },
        }));
      }).catch(error => dispatch(requestQuestionnaireFailure(error)));
  };

export const requestSubmitQuestionnaire = () => ({
  type: types.COURSE_QUEST_SUBMIT_REQUEST,
});
export const requestSubmitQuestionnaireSuccess = () => ({
  type: types.COURSE_QUEST_SUBMIT_SUCCESS,
});
export const requestSubmitQuestionnaireFailure = questionnaireError => ({
  type: types.COURSE_QUEST_SUBMIT_FAILURE,
  questionnaireError,
});

const convertFormData = (
  formData,
  answerRoot,
  questionnaire,
  CourseQuestionnaireAnswers,
) => {
  /* NOTE:
      CHECKBOX
        * get QAAnswerValue from redux form value
        * get QAAnswerText from redux form name last group
      NOTE: ATTACHMENT questions put on hold!
      ATTACHMENT
        * on load of file send to repo
        * QAAnswerText is the file name
        * QAAnswerValue is the GUID of the file returned from repo
      TEXT_FIELDS
        * QAAnswerValue and QAAnswerText are set to the rf value
      DATE
        * set QAAnswerText to human version
        * set QAAnswerValue to datetime stamp
      RADIO AND SCALE
        * set QAAnswerText to the option label
        * set QAAnswerValue to QAInputOptionID or HrQualificationScaleEntryID
  */

  const { QAElements } = questionnaire;
  const RootAnswerID = answerRoot;

  /* Map through all the fields submited through the Redux form */
  const ansrs = map(keys(formData), (key) => {
    if (startsWith(key, 'COMMENT')) { return null; }
    const QAAnswerValue = formData[key];
    /* Split the key into its components
    example: '345-34045'
    becomes ['345', '34045'] */
    const qaKeys = key.split`-`;

    /* We use the Redux field names to identify information about the elements */
    const QAInputAnswerType = Number(qaKeys[0]);
    const QAElementID = Number(qaKeys[1]);
    const QAInputOptionLabel = qaKeys[2];

    /* QAElement information */
    const {
      QAInput, Order, Level, QAElementParentID, AllowComment,
    } = find(QAElements, ['QAElementID', QAElementID]);

    const option = QAInputAnswerType === 6 ?
      find(QAInput.HrQualificationScaleEntries, ['HrQualificationScaleEntryID', QAAnswerValue]) :
      find(QAInput.QAInputOptions, ['QAInputOptionID', QAAnswerValue]);

    const checkBoxText = atob(QAInputOptionLabel || '');
    const optionText = option ? option.HrQualificationScaleEntryName || option.QAInputOptionLabel : '';
    const QAAnswerText = checkBoxText || optionText || QAAnswerValue;
    let QAAnswerComment = null;

    if (AllowComment) {
      QAAnswerComment = formData[`COMMENT-${QAInputAnswerType}-${QAElementID}`];
    }

    /* If the Element is higher than 2 the Element is inside a group, then
    we have to set the correct QAAnswerParentID. */
    const QAAnswerParent = Level <= 2 ? RootAnswerID : find(CourseQuestionnaireAnswers, ['QAElementID', QAElementParentID]);
    const QAAnswerParentID = QAAnswerParent.QAAnswerID;

    const def = {
      QAAnswerParentID,
      QAElementID,
      QAAnswerValue,
      QAAnswerText,
      QAAnswerComment,
      QAAnswerOrder: Order,
      RootAnswerID,
    };

    return def;
  });

  return compact(ansrs);
};

export const submitQuestionnaire = (formData, onSuccess) =>
  (dispatch, getState) => {
    const {
      loginReducer: { user: { token } },
      CourseQuestionnaireReducer: {
        questionnaire,
      },
    } = getState();
    dispatch(requestSubmitQuestionnaire(questionnaire.RootQAElementID));
    const createRoot = {
      entity: 'QAAnswer',
      headers: { Authorization: `Bearer ${token}` },
      data: { QAElementID: questionnaire.RootQAElementID },
    };
    create(createRoot)
      .then(({ data }) => {
        const answerRoot = data.QAAnswerID;
        const groups = questionnaire.QAElements.filter(e => e.QAElementType === 2);

        const answerGroups = groups.map(({
          QAElementID,
          Label,
          Order,
        }) => create({
          entity: 'QAAnswer',
          headers: { Authorization: `Bearer ${token}` },
          data: {
            QAAnswerParentID: data.QAAnswerID,
            RootAnswerID: data.QAAnswerID,
            QAElementID,
            QAAnswerValue: Label,
            QAAnswerText: Label,
            QAAnswerOrder: Order,
          },
        }));
        Promise.all(answerGroups)
          .then((CEAnswers) => {
            const CourseQuestionnaireAnswers = CEAnswers.map(x => ({ ...x.data }));
            const QAAnswers = convertFormData(
              formData,
              answerRoot,
              questionnaire,
              CourseQuestionnaireAnswers,
            );
            return Promise.all(QAAnswers.map(ansData => create({ entity: 'QAAnswer', data: ansData, headers: { Authorization: `Bearer ${token}` } })))
              .then(() => {
                onSuccess(answerRoot);
                dispatch(requestSubmitQuestionnaireSuccess());
              }).catch(error => dispatch(requestSubmitQuestionnaireFailure(error)));
          }).catch(error => dispatch(requestSubmitQuestionnaireFailure(error)));
      }).catch(error => dispatch(requestSubmitQuestionnaireFailure(error)));
  };



// WEBPACK FOOTER //
// ./src/actions/CourseQuestionnaire.js